import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { X, ShoppingBag, Star, ChevronRight, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// ─── localStorage helpers ────────────────────────────────────────────────────
const DISMISS_KEY = 'fm_ads_dismissed';   // permanent dismiss per delivery
const SEEN_KEY    = 'fm_ads_seen';        // impression tracking (set of product IDs)

function isDismissed(deliveryId) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const dismissed = raw ? JSON.parse(raw) : [];
    return dismissed.includes(String(deliveryId));
  } catch {
    return false;
  }
}

function persistDismiss(deliveryId) {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    const dismissed = raw ? JSON.parse(raw) : [];
    if (!dismissed.includes(String(deliveryId))) {
      dismissed.push(String(deliveryId));
      // Keep only last 50 entries to avoid bloat
      const trimmed = dismissed.slice(-50);
      localStorage.setItem(DISMISS_KEY, JSON.stringify(trimmed));
    }
  } catch { /* silent */ }
}

function getSeenIds() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function markSeen(productIds) {
  try {
    const seen = getSeenIds();
    productIds.forEach(id => seen.add(String(id)));
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen]));
  } catch { /* silent */ }
}

// ─── Analytics ───────────────────────────────────────────────────────────────
async function logEvent(deliveryId, productId, eventType, isFeatured, placement) {
  try {
    await axios.post(`${API_URL}/api/ads/event`, {
      delivery_id: deliveryId,
      product_id: productId,
      event_type: eventType,
      is_featured: isFeatured,
      placement,
    });
  } catch { /* analytics should never break the UI */ }
}

// ─── Single Ad Card ───────────────────────────────────────────────────────────
function AdCard({ ad, deliveryId, placement, index }) {
  const [imgError, setImgError] = useState(false);
  const [visible, setVisible] = useState(false);

  // Staggered entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), index * 80);
    return () => clearTimeout(timer);
  }, [index]);

  const handleClick = () => {
    if (!ad.cta_url) return;
    logEvent(deliveryId, ad.product_id, 'click', ad.is_featured, placement);
    window.open(ad.cta_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleClick}
      className={`flex-shrink-0 group ${ad.cta_url ? 'cursor-pointer' : 'cursor-default'}`}
      style={{
        width: '140px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
      }}
    >
      {/* Card */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 active:scale-95">

        {/* Image */}
        <div className="relative w-full bg-slate-100 overflow-hidden" style={{ height: '110px' }}>
          {ad.is_featured && (
            <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-0.5 bg-brand-gold text-white text-[9px] font-bold font-montserrat px-1.5 py-0.5 rounded-full">
              <Sparkles className="w-2.5 h-2.5" />
              <span>Destacado</span>
            </div>
          )}
          {ad.image_url && !imgError ? (
            <img
              src={ad.image_url}
              alt={ad.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary/5 to-brand-gold/10">
              <ShoppingBag className="w-8 h-8 text-brand-primary/30" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-2.5">
          <p className="text-[11px] text-slate-500 font-montserrat truncate mb-0.5">
            {ad.seller_name}
          </p>
          <p className="text-xs font-semibold text-slate-800 font-montserrat leading-tight mb-1.5 line-clamp-2" style={{ minHeight: '28px' }}>
            {ad.title}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-brand-primary font-montserrat">
              S/ {parseFloat(ad.price).toFixed(2)}
            </span>
            <div className="w-5 h-5 rounded-full bg-brand-primary/10 flex items-center justify-center">
              <ChevronRight className="w-3 h-3 text-brand-primary" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
/**
 * RecommendedProductsRow
 *
 * Props:
 *   deliveryId  {number}  - The current delivery ID (used for relevance + analytics)
 *   placement   {string}  - Where the ad is shown: 'main' | 'my_delivery' | 'store_info' | 'control_tower'
 *   compact     {boolean} - Use compact mode (less padding) for tight layouts like ControlTower
 */
export function RecommendedProductsRow({ deliveryId, placement = 'main', compact = false }) {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);
  const impressionLogged = useRef(false);

  // Check localStorage for permanent dismiss on mount
  useEffect(() => {
    if (isDismissed(deliveryId)) {
      setDismissed(true);
      setLoading(false);
      return;
    }
    fetchAds();
  }, [deliveryId]);

  // Log impressions once ads are visible
  useEffect(() => {
    if (ads.length > 0 && !impressionLogged.current) {
      impressionLogged.current = true;
      const unseenAds = ads.filter(ad => !getSeenIds().has(String(ad.product_id)));
      unseenAds.forEach(ad => {
        logEvent(deliveryId, ad.product_id, 'impression', ad.is_featured, placement);
      });
      markSeen(ads.map(a => a.product_id));
    }
  }, [ads]);

  const fetchAds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/final_user/deliveries/${deliveryId}/ads`);
      const adsData = (res.data.ads || []).filter(ad => ad.cta_url);
      if (adsData.length > 0) {
        setAds(adsData);
        // Animate in after short delay
        setTimeout(() => setVisible(true), 100);
      }
    } catch (err) {
      console.error('[RecommendedProductsRow] Failed to fetch ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = useCallback(() => {
    // Log dismiss event
    logEvent(deliveryId, null, 'dismiss', false, placement);
    // Persist to localStorage — survives page reload and future sessions
    persistDismiss(deliveryId);
    setDismissed(true);
  }, [deliveryId, placement]);

  // Don't render anything if dismissed, loading, or no ads
  if (dismissed || loading || ads.length === 0) return null;

  const hasFeatured = ads.some(a => a.is_featured);

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
      }}
      className={`w-full ${compact ? 'py-3' : 'py-4'}`}
    >
      {/* Header row */}
      <div className={`flex items-center justify-between ${compact ? 'px-3 mb-2' : 'px-4 mb-3'}`}>
        <div className="flex items-center gap-2">
          {hasFeatured ? (
            <div className="w-5 h-5 rounded-md bg-brand-gold flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-3 h-3 text-white" />
            </div>
          ) : (
            <div className="w-5 h-5 rounded-md bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-3 h-3 text-brand-primary" />
            </div>
          )}
          <span className={`font-bold text-slate-700 font-montserrat ${compact ? 'text-xs' : 'text-sm'}`}>
            También te puede interesar
          </span>
        </div>

        <button
          onClick={handleDismiss}
          className="flex items-center gap-1 text-slate-400 hover:text-slate-600 transition-colors active:scale-90 p-1 rounded-lg hover:bg-slate-100"
          title="Cerrar sugerencias"
          aria-label="Cerrar sugerencias de productos"
        >
          <X className="w-3.5 h-3.5" />
          <span className="text-[10px] font-montserrat">Cerrar</span>
        </button>
      </div>

      {/* Horizontal scroll row */}
      <div
        className={`flex gap-3 overflow-x-auto ${compact ? 'px-3' : 'px-4'}`}
        style={{
          scrollbarWidth: 'none',
          WebkitOverflowScrolling: 'touch',
          msOverflowStyle: 'none',
        }}
      >
        {ads.map((ad, i) => (
          <AdCard
            key={ad.product_id}
            ad={ad}
            deliveryId={deliveryId}
            placement={placement}
            index={i}
          />
        ))}
        {/* Right fade sentinel for visual affordance */}
        <div className="flex-shrink-0 w-4" />
      </div>

      {/* Subtle "publicidad" label */}
      <p className={`text-slate-400 font-montserrat ${compact ? 'px-3 mt-1.5 text-[9px]' : 'px-4 mt-2 text-[10px]'}`}>
        Publicidad · Fast Max Marketplace
      </p>
    </div>
  );
}

export default RecommendedProductsRow;